import { isNonEmpty } from '@sgme/fp';
import { mapValues } from 'lodash-es';
import type { AddonManagementFormResult } from 'models/forms/AddonManagementFormResult';
import type { Action } from 'state/actions';
import { patchKey, patchKeys } from 'state/utils/collection';

import * as actionTypes from '../addonManagementForm.actionTypes';
import { isReadOnlyForm } from '../addonManagementForm.helpers';
import type { AddonManagementFormInfos, AddonManagementFormInfosStateMap } from './infos.model';

export function infosReducer(
  state: AddonManagementFormInfosStateMap = {},
  action: Action,
): AddonManagementFormInfosStateMap {
  switch (action.type) {
    case actionTypes.ADDON_MANAGEMENT_FORM_SELECT_TOGGLED:
      return patchKey(state, action.formId, previousState => ({
        isSelected: !previousState.isSelected,
      }));
    case actionTypes.ADDON_DEFAULTING_TOGGLED:
      return patchKey(state, action.formId, previousState => ({
        defaultingInAddOn: !previousState.defaultingInAddOn,
      }));
    case actionTypes.ADDON_MANAGEMENT_FORM_HEADER_CHECKBOX_TOGGLE: {
      return Object.values(state).some(form => !isReadOnlyForm(form.status) && !form.isSelected)
        ? mapValues(state, form => (isReadOnlyForm(form.status) ? form : { ...form, isSelected: true }))
        : mapValues(state, form => (isReadOnlyForm(form.status) ? form : { ...form, isSelected: false }));
    }
    case actionTypes.ADDON_MANAGEMENT_FORM_UNCHECK_ALL_CHECKBOX: {
      return mapValues(state, form => (isReadOnlyForm(form.status) ? form : { ...form, isSelected: false }));
    }
    case actionTypes.ADDON_MANAGEMENT_FORMS_RECEIVED:
      return Object.fromEntries(
        action.forms.map(form => [form.negotiationId, createFormInfos(form), form.fieldsMetadata]),
      );
    case actionTypes.ADDON_MANAGEMENT_QUOTE_REQUESTED:
      return patchKeys(state, action.formIds, () => ({
        status: 'QUOTING' as const,
        isSelected: false,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_ADD_TO_QUEUE:
      return Object.entries(action.formIdsToQuoteIds).reduce(
        (acc, [formId, quoteId]) =>
          patchKey(acc, formId, () => ({
            status: 'QUOTING' as const,
            isSelected: false,
            lastQuoteId: quoteId,
          })),
        state,
      );
    case actionTypes.ADDON_MANAGEMENT_QUOTE_RESULT_FAILED:
      return patchKey(state, action.formId, () => ({
        isSelected: false,
        status: 'UNKNOWN_ERROR' as const,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_REQUEST_FAILED:
      return action.forms.reduce(
        (result: AddonManagementFormInfosStateMap, form: { formId: string; newVersion?: number }) =>
          patchKey(result, form.formId, () => ({
            status: 'UNKNOWN_ERROR' as const,
          })),
        state,
      );
    case actionTypes.ADDON_MANAGEMENT_FORM_ENRICHMENT_FAILED: {
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'PATCH_ERROR' as const,
      }));
    }
    case 'FORM_ENRICHMENT_REQUESTED': {
      if (action.workspace !== 'addonManagement') {
        return state;
      }
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'PATCHING' as const,
      }));
    }
    case actionTypes.ADDON_MANAGEMENT_FORM_ENRICHMENT_RECEIVED: {
      const { data, fieldsMetadata, formId, actions, validationErrors } = action;
      return patchKey(state, formId, info => ({
        ...info,
        data,
        fieldsMetadata,
        formId,
        actions,
        validationErrors,
        status: isNonEmpty(validationErrors) ? ('NOT_VALID' as const) : ('READY' as const),
      }));
    }
    case actionTypes.ADDON_MANAGEMENT_QUOTE_RECEIVED: {
      return patchKey(state, action.formId, () => ({
        status: isNonEmpty(action.quote.rejections) ? ('REJECTED' as const) : ('QUOTED' as const),
      }));
    }
    case actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REQUESTED:
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'EXECUTING' as const,
        isSelected: false,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_DONE:
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'EXECUTED' as const,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REQUEST_REJECTED:
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'REJECTED' as const,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_REJECTED:
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'REJECTED' as const,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_TRADE_FAILED:
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'UNKNOWN_ERROR' as const,
      }));
    case actionTypes.ADDON_MANAGEMENT_QUOTE_EXPIRED:
      return patchKey(state, action.formId, info => ({
        ...info,
        status: 'READY' as const,
      }));
    default:
      return state;
  }
}

function createFormInfos(form: AddonManagementFormResult): AddonManagementFormInfos {
  const { actions, negotiationId, quoteQuery, errors, historicalTrades } = form;
  return {
    actions,
    isSelected: false,
    defaultingInAddOn: false,
    negotiationId,
    quoteQuery,
    status: isNonEmpty(errors) ? ('NOT_VALID' as const) : ('READY' as const),
    lastQuoteId: '',
    validationErrors: errors,
    historicalTrades: historicalTrades,
  };
}
